import Flickity from 'flickity';
import 'flickity-fade'; // This modifies Flickity globally by adding the fade functionality
import imagesLoaded from 'imagesloaded';

class Carousel {
    constructor(element) {
        this.el = element;
        this.carouselElement = this.el.querySelector('.flickity-carousel');
        this.carouselElementClassList = this.carouselElement.classList;
        this.progressBarContainer = element.querySelector('.carousel-progress');

        if (this.progressBarContainer) {
            this.progressBar = this.progressBarContainer.querySelector('.carousel-progress-bar');
        }

        // Settings
        this.autoPlayTime = this.carouselElementClassList.contains('_autoplay') ? 5000 : false;
        this.dots = this.carouselElementClassList.contains('_dots');
        this.groupCells = this.carouselElementClassList.contains('_group-cells');
        this.lazyLoad = this.carouselElementClassList.contains('_lazyload');
        this.wrapAround = this.carouselElementClassList.contains('_wrap-around');
        this.fade = this.carouselElementClassList.contains('_fade');

        // Vars
        this.timerStart = null;
        this.animationFrame = null;
        this.isPaused = false;
        this.usingAutoplayProgress = !!this.autoPlayTime;

        this.prevButton = this.el.querySelector('.carousel-button-prev');
        this.nextButton = this.el.querySelector('.carousel-button-next');

        let hasCarouselInit = false;
        imagesLoaded(this.el, { background: false })
            .on('progress', (instance, image) => {
                if (!hasCarouselInit && image.isLoaded) {
                    hasCarouselInit = true;
                    this.initCarousel();
                }
            })
            .on('always', (instance, image) => {
                if (!hasCarouselInit) {
                    // Load carousel if no image has loaded, this is due to failed image loading
                    this.initCarousel();
                }
            });
    }

    initCarousel() {
        this.flickity = new Flickity(this.carouselElement, {
            cellAlign: 'left',
            contain: true,
            draggable: true,
            fade: this.fade,
            groupCells: this.groupCells,
            lazyLoad: false,
            pageDots: this.dots,
            prevNextButtons: false,
            wrapAround: this.wrapAround,
            selectedAttraction: 0.025, // 0.02
            friction: 0.28, // 0.4
            on: {
                'dragStart': () => {
                    this.carouselElement.style.pointerEvents = 'none';
                },
                'dragEnd': () => {
                    this.carouselElement.style.pointerEvents = 'all';
                }
            }
        });

        // this.flickity.on('scroll', () => this.updateProgress);
        this.flickity.on('change', () => {
            this.updateProgress();
            this.resetTimer();
        });
        this.flickity.on('dragStart', () => this.switchToSlideProgress());
        this.flickity.on('pointerDown', () => this.switchToSlideProgress());
        this.flickity.on('settle', () => this.updateProgress());

        if (this.autoPlayTime) {
            this.startAutoplayTimer();
            // this.addHoverListeners();
        } else {
            this.updateProgress();
        }

        this.addNavigationButtons();
        this.addExternalNavigation(this.el, this.flickity);
    }

    updateProgress() {
        if (this.progressBarContainer) {
            if (this.usingAutoplayProgress) return;

            const progress = (this.flickity.selectedIndex + 1) / this.flickity.slides.length * 100;
            this.progressBar.style.width = `${progress}%`;
            this.progressBar.style.transitionProperty = 'all';
            this.progressBar.style.transitionTimingFunction = 'cubic-bezier(0.4, 0, 0.2, 1)';
            this.progressBar.style.transitionDuration = '300ms';
        }
    }

    startAutoplayTimer() {
        if (this.progressBarContainer) {
            this.timerStart = performance.now();
            this.animateTimer();
        }
    }

    animateTimer() {
        if (this.progressBarContainer) {
            const update = (now) => {
                if (this.isPaused || !this.usingAutoplayProgress) return;

                const elapsed = now - this.timerStart;
                const progress = Math.min((elapsed / this.autoPlayTime) * 100, 100);
                this.progressBar.style.width = `${progress}%`;

                if (progress < 100) {
                    this.animationFrame = requestAnimationFrame(update);
                } else {
                    this.flickity.next();
                    this.resetTimer();
                }
            };
            this.animationFrame = requestAnimationFrame(update);
        }
    }

    resetTimer() {
        if (this.progressBarContainer) {
            if (!this.autoPlayTime || !this.usingAutoplayProgress) return;

            cancelAnimationFrame(this.animationFrame);

            this.progressBar.style.width = '0%';
            this.timerStart = performance.now();
            this.animateTimer();
        }
    }

    addHoverListeners() {
        this.carouselElement.addEventListener('mouseenter', () => {
            this.isPaused = true;
            cancelAnimationFrame(this.animationFrame);
        });

        this.carouselElement.addEventListener('mouseleave', () => {
            this.isPaused = false;
            this.timerStart = performance.now();
            this.animateTimer();
        });
    }

    switchToSlideProgress() {
        this.usingAutoplayProgress = false;
        cancelAnimationFrame(this.animationFrame);
        this.updateProgress();
    }

    addNavigationButtons() {
        if (this.prevButton) {
            this.prevButton.addEventListener('click', () => {
                this.flickity.previous();
                this.switchToSlideProgress();
            });
        }

        if (this.nextButton) {
            this.nextButton.addEventListener('click', () => {
                this.flickity.next();
                this.switchToSlideProgress();
            });
        }
    }

    addExternalNavigation(carousel, instance) {
        const navAttr = 'data-carousel-target';

        document.querySelectorAll(`[${navAttr}]`).forEach(button => {
            button.addEventListener('click', (event) => {
                const slideIndex = parseInt(button.getAttribute(navAttr), 10);

                if (!isNaN(slideIndex)) {
                    if (instance) {
                        instance.select(slideIndex);
                        carousel.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                }
            });
        });
    }

    static initAll() {
        document.querySelectorAll('.carousel').forEach(carousel => new Carousel(carousel));
    }
}

export default Carousel;